/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { notify } from '../../common/functions/react-toastify.function';
import {
  getDocumentBlob,
  revokeBlobUrl,
} from '../ApprovalPersonsInvoiceManagement/functions/get_document_blob.function';
import { getSystemPoInvoiceAuditLogs } from './functions/get_system_po_invoice_audit_logs.function';
import { getSystemApproverRoles } from '../ApprovalPersonsSystemInvoiceManagement/functions/get_system_approver_roles.function';
import {
  assignSystemWorkflowInvoice,
  startSystemWorkflowInvoiceById,
  stopSystemWorkflowInvoice,
  rejectSystemWorkflowInvoice,
} from './functions/system_po_workflow_invoice_mutations.function';
import POInvoiceHeader from '../POWorkflowManagement/components/po_invoice_header.component';
import InvoiceDetailsCard from '../ApprovalPersonsInvoiceManagement/components/invoice_details_card.component';
import CommentsSection from '../ApprovalPersonsSystemInvoiceManagement/components/comments_section.component';
import DocumentsList from '../ApprovalPersonsInvoiceManagement/components/documents_list.component';
import DocumentPreview from '../ApprovalPersonsInvoiceManagement/components/document_preview.component';
import InvoiceHistoryModal from '../ApprovalPersonsInvoiceManagement/components/invoice_history_modal.component';
import {
  getCurrentApproverEmail,
  getPreviousApproverEmailFromAuditLogs,
} from '../../common/functions/derive_invoice_approver_emails.function';
import { buildPoInvoiceSummary } from '../ApprovalPersonsInvoiceManagement/utils/po_invoice_summary.util';
import { getAllSystemVendorInvoicesForWorkflow } from './functions/get_all_system_vendor_invoices.function';
import { runWithUnsavedCommentsGuard } from '../../common/functions/unsaved_comments_guard.function';

const toNumber = (v) => {
  const n = Number(
    String(v ?? '')
      .replace(/,/g, '')
      .trim()
  );
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return '0.00';
  }
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeInvoicePayload = (inv) => {
  if (!inv) {
    return null;
  }
  const base = inv._originalData || inv;
  const id = base.id ?? base.invoice_id ?? inv.id;
  const rawAssigned = inv.assigned_role || inv.assignedRole || base.assigned_role || null;
  const rawPrevious = inv.previous_assigned_role || base.previous_assigned_role || null;
  const assigned_role = rawAssigned?.id || rawAssigned?.name ? rawAssigned : null;
  const previous_assigned_role = rawPrevious?.id || rawPrevious?.name ? rawPrevious : null;
  const merged = {
    ...base,
    id,
    // Prefer PO/system claim type for Planning routing (IPRAN/OTN/FTTS)
    type: inv.type || base.type || inv.invoice_type_detail || base.invoice_type_detail || null,
    invoice_type: inv.invoice_type || base.invoice_type || null,
    assigned_role,
    previous_assigned_role,
    vendor_remarks: base.vendor_remarks ?? inv.vendor_remarks ?? '',
    reason_of_rejection: base.reason_of_rejection ?? inv.reason_of_rejection ?? null,
  };
  return { ...merged, _originalData: merged };
};

const SystemPOInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedInvoiceData = location.state?.invoiceData;
  const poNumber = location.state?.poNumber || '';
  const invoices = location.state?.invoices || [];
  const poDetails = location.state?.poDetails || null;
  const fromTab = location.state?.fromTab || '';
  const isViewOnlyFromAllInvoices = fromTab === 'all-invoices-full';

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('System Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [invoiceData, setInvoiceData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [approverRoles, setApproverRoles] = useState([]);
  const [filteredApproverRoles, setFilteredApproverRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [assigningRole, setAssigningRole] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const commentsSectionRef = useRef(null);
  const [poInvoicesForSummary, setPoInvoicesForSummary] = useState(null);
  const [poSummaryLoading, setPoSummaryLoading] = useState(true);

  const [invoiceSource, setInvoiceSource] = useState(() =>
    normalizeInvoicePayload(passedInvoiceData)
  );

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    setInvoiceSource(normalizeInvoicePayload(passedInvoiceData));
  }, [passedInvoiceData]);

  const invoicePayload = useMemo(
    () => invoiceSource || normalizeInvoicePayload(passedInvoiceData),
    [invoiceSource, passedInvoiceData]
  );
  const rawInvoice = invoicePayload?._originalData || invoicePayload || {};

  const currentInvoiceNumber =
    rawInvoice.invoice_number || rawInvoice.invoiceNumber || invoiceData?.invoiceNumber || '';
  const currentInvoiceId = rawInvoice.id ?? rawInvoice.invoice_id ?? null;

  const transformAuditLogsForTimeline = (logs) => {
    const filteredLogs = (logs || []).filter(
      (log) => (log.performed_by_role || '').toUpperCase() !== 'SYSTEM'
    );
    return filteredLogs.map((log) => {
      let performerName = log.performed_by_role || log.performed_by_username || 'Unknown';
      if (performerName.toUpperCase() === 'USER') {
        performerName = 'Vendor';
      }

      let summary = '';
      const actionCode = log.action_code || '';
      const actionDetails = log.action_details || {};

      switch (actionCode) {
        case 'CREATED':
          summary = 'Invoice created';
          if (actionDetails.vendor) {
            summary += ` by ${actionDetails.vendor}`;
          }
          break;
        case 'ASSIGNED':
          summary = 'Invoice assigned';
          if (log.assigned_role?.name) {
            summary += ` to ${log.assigned_role.name}`;
          } else if (actionDetails.assigned_to_role_id) {
            summary += ` to role ID ${actionDetails.assigned_to_role_id}`;
          }
          break;
        case 'APPROVED':
          summary = 'Invoice approved';
          if (actionDetails.new_role_name) {
            summary += ` and forwarded to ${actionDetails.new_role_name}`;
          } else if (actionDetails.reason === 'No next role available - final approval') {
            summary += ' (final approval)';
          }
          break;
        case 'REJECTED':
        case 'PENDING_REJECTION':
          summary = actionCode === 'PENDING_REJECTION' ? 'Pending rejection' : 'Invoice rejected';
          break;
        case 'STOPPED':
          summary = 'Invoice stopped';
          break;
        case 'STARTED':
          summary = 'Invoice started';
          break;
        case 'SUBMITTED':
          summary = 'Invoice submitted';
          break;
        case 'UPDATED':
          summary = 'Invoice updated';
          break;
        default:
          summary = actionCode ? `Invoice ${actionCode.toLowerCase()}` : 'Invoice action';
      }

      if (actionDetails.comment) {
        summary += ` (Comment: ${actionDetails.comment})`;
      }

      return {
        timeStamp: log.created_at || '',
        username: performerName,
        summary,
        details: actionDetails,
      };
    });
  };

  const mapInvoiceData = (payload) => {
    const formatDate = (dateString) => {
      if (!dateString) {
        return '';
      }
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch (e) {
        return dateString;
      }
    };

    const inv = payload._originalData || payload;
    const previousRole = inv?.previous_assigned_role || payload?.previous_assigned_role || null;
    const assignedRole =
      inv?.assigned_role || payload?.assigned_role || payload?.assignedRole || null;
    const previousApprover =
      previousRole?.name || (previousRole?.id ? payload?.previousApprover : '') || '';
    const currentApprover = assignedRole?.name || payload?.currentApprover || '';

    const poSummary = buildPoInvoiceSummary({
      rawInvoice: inv,
      poInvoicesList: poInvoicesForSummary || [],
      poDetails,
      allowRemainingFallback: !poSummaryLoading,
    });

    const mappedInvoiceData = {
      invoiceNumber: inv.invoice_number || inv.invoiceNumber || '',
      title: inv.invoice_number || inv.invoiceNumber || '',
      invoiceNo: inv.invoice_number || inv.invoiceNumber || '',
      category: inv.category || '',
      vendorName: inv.vendor || inv.invoice_vendor || '',
      invoiceDate: formatDate(inv.created_at || inv.invoice_created_at),
      region: inv.region || '',
      poNumber: inv.po_number || inv.poNumber || poNumber || '',
      poStatus: 'Active',
      approvalStatus: inv.status
        ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1).toLowerCase()
        : '',
      status: inv.status || '',
      vendorRemarks: inv.vendor_remarks || inv.vendorRemarks || '',
      previousApprover,
      currentApprover,
      invoiceType: inv.invoice_type || inv.invoiceType || '',
      routingType: inv.type || inv.invoice_type_detail || inv.invoice_type || inv.invoiceType || '',
      poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
      alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
      poInvoicesCount: poSummary.poInvoicesCount,
      reasonOfRejection: poSummary.reasonOfRejection,
    };

    const mappedDocuments = [];
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';

    if (inv.po_file_path) {
      const fileName = inv.po_file_path.split('/').pop() || 'PO File.pdf';
      mappedDocuments.push({
        id: 'po-file',
        name: 'PO File',
        fileName,
        filePath: inv.po_file_path,
        url: `${apiBaseUrl}/systemVendorInvoiceManagement/invoice/document/${inv.po_file_path}`,
        type: 'application/pdf',
      });
    }

    const other = inv.other_documents_paths;
    if (Array.isArray(other)) {
      other.forEach((docPath, index) => {
        const fileName = docPath.split('/').pop() || `Document ${index + 1}`;
        const lower = String(fileName).toLowerCase();
        let type = 'application/octet-stream';
        if (lower.endsWith('.pdf')) {
          type = 'application/pdf';
        } else if (lower.endsWith('.xlsx')) {
          type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (lower.endsWith('.xls')) {
          type = 'application/vnd.ms-excel';
        } else if (lower.endsWith('.csv')) {
          type = 'text/csv';
        } else if (lower.endsWith('.msg')) {
          type = 'application/vnd.ms-outlook';
        }
        mappedDocuments.push({
          id: `other-doc-${index}`,
          name: fileName,
          fileName,
          filePath: docPath,
          url: `${apiBaseUrl}/systemVendorInvoiceManagement/invoice/document/${docPath}`,
          type,
        });
      });
    }

    return { mappedInvoiceData, mappedDocuments };
  };

  useEffect(() => {
    const poNum = String(
      poNumber || invoicePayload?.po_number || invoicePayload?.poNumber || ''
    ).trim();
    if (!poNum) {
      setPoInvoicesForSummary(null);
      setPoSummaryLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadAllForPo = async () => {
      setPoSummaryLoading(true);
      try {
        const list = await getAllSystemVendorInvoicesForWorkflow();
        const forPo = (Array.isArray(list) ? list : []).filter(
          (inv) => String(inv.po_number || inv.poNumber || '').trim() === poNum
        );
        if (!cancelled) {
          setPoInvoicesForSummary(forPo);
          setPoSummaryLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setPoInvoicesForSummary(
            Array.isArray(invoices) ? invoices.map((i) => i._originalData || i) : []
          );
          setPoSummaryLoading(false);
        }
      }
    };

    loadAllForPo();
    return () => {
      cancelled = true;
    };
  }, [poNumber, invoicePayload?.po_number, invoicePayload?.poNumber, invoices]);

  useEffect(() => {
    if (!invoicePayload || poSummaryLoading) {
      return;
    }
    const rawInv = invoicePayload._originalData || invoicePayload;
    const poSummary = buildPoInvoiceSummary({
      rawInvoice: rawInv,
      poInvoicesList: poInvoicesForSummary || [],
      poDetails,
      allowRemainingFallback: false,
    });
    setInvoiceData((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
        alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
        poInvoicesCount: poSummary.poInvoicesCount,
        reasonOfRejection: poSummary.reasonOfRejection,
      };
    });
  }, [invoicePayload, poInvoicesForSummary, poDetails, poSummaryLoading]);

  useEffect(() => {
    const loadApproverRoles = async () => {
      setLoadingRoles(true);
      try {
        const roles = await getSystemApproverRoles();
        setApproverRoles(roles);
      } catch (err) {
        console.error('Error loading system approver roles:', err);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadApproverRoles();
  }, []);

  useEffect(() => {
    if (!approverRoles.length || !invoiceData) {
      setFilteredApproverRoles([]);
      return;
    }
    const invoiceRegion = invoiceData.region || '';
    // Same as BE approve routing: prefer invoice.type (IPRAN/OTN/FTTS) over invoice_type
    const normalizeType = (val) => {
      if (val == null || String(val).trim() === '') {
        return null;
      }
      return String(val).trim().toUpperCase().replace(/-/g, '_');
    };
    let invoiceType = normalizeType(
      invoicePayload?.type ||
        invoicePayload?._originalData?.type ||
        invoicePayload?.invoice_type_detail ||
        invoicePayload?._originalData?.invoice_type_detail ||
        invoiceData?.routingType ||
        invoicePayload?.invoice_type ||
        invoicePayload?._originalData?.invoice_type
    );
    const filtered = approverRoles.filter((role) => {
      if (role.category === 'RO') {
        if (role.region == null) {
          return true;
        }
        return role.region === invoiceRegion;
      }
      if (role.category === 'PLANNING') {
        const roleType = normalizeType(role.invoice_type);
        // Typed Planning roles (L2 FTTS/IPRAN/OTN): only show matching invoice type
        if (roleType) {
          return Boolean(invoiceType) && roleType === invoiceType;
        }
        // Untyped Planning roles (e.g. L1): hide if a typed peer exists for this invoice type
        const hasMatchingTypeRole = approverRoles.some(
          (r) =>
            r.category === 'PLANNING' &&
            r.name === role.name &&
            normalizeType(r.invoice_type) === invoiceType &&
            r.id !== role.id
        );
        return !hasMatchingTypeRole || !invoiceType;
      }
      return true;
    });

    const getRoleLevel = (name = '') => {
      const match = String(name).match(/l\s*(\d+)/i);
      return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
    };
    const categoryOrder = (category) => {
      const c = String(category || '').toUpperCase();
      if (c === 'RO') {
        return 0;
      }
      if (c === 'PLANNING') {
        return 1;
      }
      return 2;
    };

    filtered.sort((a, b) => {
      const byCategory = categoryOrder(a.category) - categoryOrder(b.category);
      if (byCategory !== 0) {
        return byCategory;
      }
      const byLevel = getRoleLevel(a.name) - getRoleLevel(b.name);
      if (byLevel !== 0) {
        return byLevel;
      }
      return String(a.name || '').localeCompare(String(b.name || ''));
    });

    setFilteredApproverRoles(filtered);
  }, [approverRoles, invoiceData, invoicePayload]);

  useEffect(() => {
    const loadInvoiceData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (invoicePayload) {
          const { mappedInvoiceData, mappedDocuments } = mapInvoiceData(invoicePayload);
          setInvoiceData(mappedInvoiceData);
          setDocuments(mappedDocuments);
          const existingReason =
            invoicePayload?.reason_of_rejection ||
            invoicePayload?._originalData?.reason_of_rejection ||
            '';
          setRejectReason((existingReason || '').toString());
          if (mappedDocuments.length > 0) {
            setSelectedDocumentIndex(0);
            setSelectedDocument(mappedDocuments[0]);
            setShowDocumentPreview(true);
          }
        } else {
          setError('Invoice data not found. Please go back and try again.');
        }
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };
    loadInvoiceData();
  }, [invoicePayload, poNumber]);

  const fetchAuditLogs = useCallback(async () => {
    if (!currentInvoiceNumber) {
      return;
    }
    try {
      setLoadingAuditLogs(true);
      const response = await getSystemPoInvoiceAuditLogs(currentInvoiceNumber);
      const logs = response?.response && Array.isArray(response.response) ? response.response : [];
      setAuditLogs(logs);
    } catch (e) {
      console.error('Error loading system audit logs:', e);
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [currentInvoiceNumber]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  useEffect(() => {
    if (!invoiceData) {
      return;
    }

    const hasPreviousApprover = Boolean(
      String(invoiceData.previousApprover || '').trim() ||
        invoicePayload?.previous_assigned_role?.id ||
        invoicePayload?.previous_assigned_to_roole_id
    );

    // Only attach previous email when a real previous role exists.
    // Do not invent previous approver from unrelated audit APPROVED logs.
    const previousApproverEmail = hasPreviousApprover
      ? getPreviousApproverEmailFromAuditLogs(auditLogs)
      : '';
    const currentApproverEmail = getCurrentApproverEmail(
      auditLogs,
      invoiceData.currentApprover,
      invoicePayload
    );
    setInvoiceData((prev) => {
      if (!prev) {
        return prev;
      }
      if (
        prev.previousApproverEmail === previousApproverEmail &&
        prev.currentApproverEmail === currentApproverEmail
      ) {
        return prev;
      }
      return {
        ...prev,
        previousApproverEmail,
        currentApproverEmail,
      };
    });
  }, [
    auditLogs,
    invoiceData?.invoiceNumber,
    invoiceData?.currentApprover,
    invoiceData?.previousApprover,
    invoicePayload,
  ]);

  const serviceItemsData = useMemo(() => {
    const items = rawInvoice.service_items_data || rawInvoice.rings_data || [];
    return Array.isArray(items) ? items : [];
  }, [rawInvoice]);

  const totalInvoiceAmount = useMemo(() => {
    return (serviceItemsData || []).reduce((sum, it) => {
      const qty = toNumber(it?.invoiced_qty ?? it?.invoicedQty);
      const unit = toNumber(it?.unit_price ?? it?.unitPrice);
      return sum + qty * unit;
    }, 0);
  }, [serviceItemsData]);

  const handleBack = () => {
    runWithUnsavedCommentsGuard(
      commentsSectionRef,
      () => {
        if (isViewOnlyFromAllInvoices) {
          navigate('/systemPoWorkflowManagement');
          return;
        }
        navigate('/systemPoWorkflowManagement/invoices', {
          state: { poNumber, invoices, poDetails },
        });
      },
      { isNavigation: true }
    );
  };

  const handleHistory = () => {
    setHistoryModalOpen(true);
  };

  const handleAssignTo = async (role) => {
    if (!currentInvoiceNumber || assigningRole) {
      return;
    }
    setAssigningRole(true);
    try {
      const response = await assignSystemWorkflowInvoice(currentInvoiceNumber, {
        assigned_to: role.id,
        role_name: role.name,
      });
      notify('success', response?.message || 'Invoice assigned successfully');
      navigate('/systemPoWorkflowManagement/invoices', { state: { poNumber, invoices } });
    } catch (err) {
      handleApiError(err);
    } finally {
      setAssigningRole(false);
    }
  };

  const handleStart = async () => {
    if (!currentInvoiceId || loadingAction) {
      return;
    }
    setLoadingAction('start');
    try {
      await startSystemWorkflowInvoiceById(currentInvoiceId, '');
      navigate('/systemPoWorkflowManagement/invoices', { state: { poNumber, invoices } });
    } catch (err) {
      setLoadingAction(null);
    }
  };

  const handleStop = async () => {
    if (!currentInvoiceNumber || loadingAction) {
      return;
    }
    setLoadingAction('stop');
    try {
      await stopSystemWorkflowInvoice(currentInvoiceNumber, '');
      navigate('/systemPoWorkflowManagement/invoices', { state: { poNumber, invoices } });
    } catch (err) {
      setLoadingAction(null);
    }
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectClose = () => {
    if (loadingAction === 'reject') {
      return;
    }
    setRejectDialogOpen(false);
  };

  const handleRejectConfirm = async () => {
    if (!currentInvoiceNumber || loadingAction === 'reject') {
      return;
    }
    const trimmed = (rejectReason || '').trim();
    if (!trimmed) {
      notify('error', 'Reason of rejection is required');
      return;
    }
    setLoadingAction('reject');
    try {
      const resp = await rejectSystemWorkflowInvoice(currentInvoiceNumber, trimmed, '');
      notify('success', resp?.message || 'Invoice rejected successfully');
      navigate('/systemPoWorkflowManagement/invoices', { state: { poNumber, invoices } });
    } catch (err) {
      setLoadingAction(null);
    }
  };

  const handleDocumentClick = async (index, document) => {
    const previousBlobUrl = documentBlobUrl;
    if (previousBlobUrl) {
      revokeBlobUrl(previousBlobUrl);
    }
    setDocumentBlobUrl(null);
    setLoadingDocument(true);
    setSelectedDocumentIndex(index);
    setSelectedDocument(document);
    setShowDocumentPreview(true);
    try {
      if (document.blobUrl) {
        setDocumentBlobUrl(document.blobUrl);
        setLoadingDocument(false);
        return;
      }
      if (document.url || document.documentUrl || document.fileUrl) {
        setLoadingDocument(false);
        return;
      }
      if (document.id && currentInvoiceNumber) {
        const blobUrl = await getDocumentBlob(currentInvoiceNumber, document.id);
        setDocumentBlobUrl(blobUrl);
      }
    } catch (e) {
      console.error('Error loading document:', e);
      handleApiError(e);
    } finally {
      setLoadingDocument(false);
    }
  };

  useEffect(() => {
    return () => {
      if (documentBlobUrl) {
        revokeBlobUrl(documentBlobUrl);
      }
    };
  }, [documentBlobUrl]);

  const previewUrl = useMemo(() => {
    if (!selectedDocument) {
      return null;
    }
    const baseUrl =
      documentBlobUrl ||
      selectedDocument.blobUrl ||
      selectedDocument.url ||
      selectedDocument.documentUrl ||
      selectedDocument.fileUrl ||
      null;
    if (!baseUrl) {
      return null;
    }
    const isExcelOrCSVOrMsg =
      selectedDocument.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      selectedDocument.type === 'application/vnd.ms-excel' ||
      selectedDocument.type === 'text/csv' ||
      selectedDocument.type === 'application/vnd.ms-outlook' ||
      /\.(xlsx|xls|csv|msg)$/i.test(selectedDocument.fileName || selectedDocument.name || '');
    if (isExcelOrCSVOrMsg && !baseUrl.includes('blob:')) {
      const sep = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${sep}format=pdf`;
    }
    return baseUrl;
  }, [selectedDocument, documentBlobUrl]);

  const previewType = useMemo(() => {
    if (!selectedDocument) {
      return 'application/pdf';
    }
    const isExcelOrCSVOrMsg =
      selectedDocument.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      selectedDocument.type === 'application/vnd.ms-excel' ||
      selectedDocument.type === 'text/csv' ||
      selectedDocument.type === 'application/vnd.ms-outlook' ||
      /\.(xlsx|xls|csv|msg)$/i.test(selectedDocument.fileName || selectedDocument.name || '');
    return isExcelOrCSVOrMsg ? 'application/pdf' : selectedDocument.type || 'application/pdf';
  }, [selectedDocument]);

  if (loading) {
    return (
      <DashboardLayout padding={true}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !invoiceData) {
    return (
      <DashboardLayout padding={true}>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error || 'Invoice not found'}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout padding={true}>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            margin: '0 auto',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
          }}
        >
          <POInvoiceHeader
            invoiceNumber={invoiceData.invoiceNumber}
            onBack={handleBack}
            onHistory={handleHistory}
            onAssignTo={isViewOnlyFromAllInvoices ? undefined : handleAssignTo}
            onStart={isViewOnlyFromAllInvoices ? undefined : handleStart}
            onStop={isViewOnlyFromAllInvoices ? undefined : handleStop}
            onReject={isViewOnlyFromAllInvoices ? undefined : handleRejectClick}
            approverRoles={isViewOnlyFromAllInvoices ? [] : filteredApproverRoles}
            loadingRoles={isViewOnlyFromAllInvoices ? false : loadingRoles}
            assigningRole={isViewOnlyFromAllInvoices ? false : assigningRole}
            loadingAction={loadingAction}
            invoiceStatus={invoiceData.approvalStatus}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InvoiceDetailsCard invoiceData={invoiceData} hideCategory />
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    mb: 2,
                    color: '#192126',
                  }}
                >
                  Service Items
                </Typography>

                <TableContainer
                  component={Paper}
                  sx={{ boxShadow: 'none', border: '1px solid #E8E9E9', borderRadius: '8px' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#344054', width: 56 }}>
                          Sr
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Code</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Item Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>PO Qty</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>
                          Claimed Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>
                          Remaining Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>
                          Invoiced Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#344054' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(serviceItemsData || []).map((it, idx) => {
                        const qty = toNumber(it?.invoiced_qty ?? it?.invoicedQty);
                        const unit = toNumber(it?.unit_price ?? it?.unitPrice);
                        const amount = qty * unit;
                        return (
                          <TableRow key={`${it?.item_code || 'item'}-${idx}`}>
                            <TableCell sx={{ color: '#667085' }}>{idx + 1}</TableCell>
                            <TableCell>{it?.item_code ?? '—'}</TableCell>
                            <TableCell>{it?.item_type ?? '—'}</TableCell>
                            <TableCell>{it?.po_qty ?? 0}</TableCell>
                            <TableCell>{it?.claimed_qty ?? 0}</TableCell>
                            <TableCell>{it?.remaining_qty ?? 0}</TableCell>
                            <TableCell>{it?.invoiced_qty ?? 0}</TableCell>
                            <TableCell>{it?.unit_price ?? '—'}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#101828' }}>
                              {formatMoney(amount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {serviceItemsData && serviceItemsData.length > 0 && (
                        <TableRow
                          sx={{
                            background:
                              'linear-gradient(90deg, rgba(189, 29, 35, 0.10) 0%, rgba(16, 185, 129, 0.10) 100%)',
                          }}
                        >
                          <TableCell colSpan={8} sx={{ fontWeight: 800, color: '#101828' }}>
                            Total Invoice Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: '#101828' }}>
                            {formatMoney(totalInvoiceAmount)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <CommentsSection
                ref={commentsSectionRef}
                invoiceId={rawInvoice.id ?? rawInvoice.invoice_id}
                commentByRoleId={rawInvoice.assigned_to_role_id}
                commentByRoleName={
                  rawInvoice.assigned_role?.name || invoicePayload?.assigned_role?.name || 'ADMIN'
                }
                userType="systemAdmin"
              />

              <Box sx={{ mt: 2 }} />
              <DocumentsList
                documents={documents}
                selectedIndex={selectedDocumentIndex}
                onDocumentClick={handleDocumentClick}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {showDocumentPreview && selectedDocument ? (
                <DocumentPreview
                  key={`syswf-doc-${selectedDocumentIndex}-${selectedDocument?.id || selectedDocumentIndex}`}
                  documentUrl={previewUrl}
                  downloadUrl={
                    documentBlobUrl ||
                    selectedDocument.blobUrl ||
                    selectedDocument.url ||
                    selectedDocument.documentUrl ||
                    selectedDocument.fileUrl ||
                    null
                  }
                  documentName={selectedDocument.name || selectedDocument.fileName || 'Document'}
                  documentType={previewType}
                  isLoading={loadingDocument}
                />
              ) : (
                <Alert severity="info">Select a document to preview.</Alert>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <InvoiceHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        invoiceId={currentInvoiceNumber}
        auditLogs={transformAuditLogsForTimeline(auditLogs)}
        isLoadingAuditLogs={loadingAuditLogs}
        onRefresh={fetchAuditLogs}
      />

      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>Reject Invoice</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please provide the reason of rejection.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason of rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={loadingAction === 'reject'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRejectClose} disabled={loadingAction === 'reject'}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectConfirm}
            disabled={loadingAction === 'reject'}
          >
            {loadingAction === 'reject' ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default SystemPOInvoiceDetails;
